import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../supabase';
import { DateTime } from 'luxon';

interface ModelContract {
  id: string;
  model_id: string;
  contracted_split: number;
  vat: boolean;
  effective_date: DateTime;
  created_at: DateTime;
  updated_at: DateTime;
}

type ModelContractCreate = Omit<ModelContract, 'id' | 'created_at' | 'updated_at'>;
type ModelContractUpdate = Partial<Omit<ModelContract, 'id' | 'model_id' | 'created_at' | 'updated_at'>>;

const SupabaseModelContractService = {
  async addModelContract(contract: ModelContractCreate): Promise<string> {
    const { data, error } = await supabase
      .from('model_contracts')
      .insert({
        model_id: contract.model_id,
        contracted_split: contract.contracted_split,
        vat: contract.vat,
        effective_date: contract.effective_date.toISO(),
      })
      .select()
      .maybeSingle();

    if (error) throw error;
    return data.id;
  },

  async getCurrentModelContract(model_id: string): Promise<ModelContract | null> {
    const today = DateTime.utc().toISODate();

    const { data, error } = await supabase
      .from('model_contracts')
      .select('*')
      .eq('model_id', model_id)
      .lte('effective_date', today)
      .order('effective_date', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }

    if (!data) return null;

    return {
      ...data,
      effective_date: DateTime.fromISO(data.effective_date, { zone: 'UTC' }),
      created_at: DateTime.fromISO(data.created_at, { zone: 'UTC' }),
      updated_at: DateTime.fromISO(data.updated_at, { zone: 'UTC' }),
    };
  },

  async getAllModelContracts(model_id: string): Promise<ModelContract[]> {
    const { data, error } = await supabase
      .from('model_contracts')
      .select('*')
      .eq('model_id', model_id)
      .order('effective_date', { ascending: false });

    if (error) throw error;

    return (data || []).map(contract => ({
      ...contract,
      effective_date: DateTime.fromISO(contract.effective_date, { zone: 'UTC' }),
      created_at: DateTime.fromISO(contract.created_at, { zone: 'UTC' }),
      updated_at: DateTime.fromISO(contract.updated_at, { zone: 'UTC' }),
    }));
  },

  async getModelContract(id: string): Promise<ModelContract | null> {
    const { data, error } = await supabase
      .from('model_contracts')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error || !data) return null;

    return {
      ...data,
      effective_date: DateTime.fromISO(data.effective_date, { zone: 'UTC' }),
      created_at: DateTime.fromISO(data.created_at, { zone: 'UTC' }),
      updated_at: DateTime.fromISO(data.updated_at, { zone: 'UTC' }),
    };
  },

  async updateModelContract(id: string, updates: ModelContractUpdate): Promise<void> {
    const dbUpdates: any = { ...updates };

    if (updates.effective_date instanceof DateTime) {
      dbUpdates.effective_date = updates.effective_date.toISO();
    }

    const { error } = await supabase
      .from('model_contracts')
      .update(dbUpdates)
      .eq('id', id);

    if (error) throw error;
  },

  async deleteModelContract(id: string): Promise<void> {
    const { error } = await supabase
      .from('model_contracts')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },
};

export const useCurrentModelContract = (model_id: string) => {
  return useQuery({
    queryKey: ['model-contracts', 'current', model_id],
    queryFn: () => SupabaseModelContractService.getCurrentModelContract(model_id),
    enabled: !!model_id,
  });
};

export const useAllModelContracts = (model_id: string) => {
  return useQuery({
    queryKey: ['model-contracts', model_id],
    queryFn: () => SupabaseModelContractService.getAllModelContracts(model_id),
    enabled: !!model_id,
  });
};

export const useModelContract = (id: string) => {
  return useQuery({
    queryKey: ['model-contracts', 'single', id],
    queryFn: () => SupabaseModelContractService.getModelContract(id),
    enabled: !!id,
  });
};

export const useAddModelContract = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: SupabaseModelContractService.addModelContract,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['model-contracts', variables.model_id] });
      queryClient.invalidateQueries({ queryKey: ['model-contracts', 'current', variables.model_id] });
    },
  });
};

export const useUpdateModelContract = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: ModelContractUpdate }) =>
      SupabaseModelContractService.updateModelContract(id, updates),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['model-contracts'] });
      queryClient.invalidateQueries({ queryKey: ['model-contracts', 'single', id] });
    },
  });
};

export const useDeleteModelContract = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => SupabaseModelContractService.deleteModelContract(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['model-contracts'] });
    },
  });
};

export default SupabaseModelContractService;
